import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { PageTitle, Spinner } from "../../components/ui/misc";
import { Panel } from "../../components/ui/Panel";
import { api } from "../../lib/api/endpoints";
import type { PendingRemovals, SyncRun } from "../../lib/api/models";
import { formatDateTime } from "../../lib/format";
import { qk } from "../../lib/queries";
import { plural } from "../clients/client-ui";
import { syncChanges } from "../gorelo/summary";
import { useSync } from "../gorelo/useSync";

const KIND = { full: "Full", quick: "Quick", client: "One client" } as const;
const result = (run: SyncRun) => (run.ok === null ? "Running" : run.ok ? "Succeeded" : "Failed");
const pendingLine = (p: PendingRemovals, deleted = "deleted") =>
  `${plural(p.clients, "client", "clients")} would be hidden, ${plural(p.sites, "site", "sites")} and ${plural(p.items, "item", "items")} ${deleted}.`;

/**
 * Admin, Gorelo (Gorelo sync §6.4): whether Gorelo is connected, the last passes, the last error, removals the
 * safety stop held back, and the clients from before the sync. The key is never entered or shown here (§7).
 */
export function GoreloAdmin() {
  const { data: status, isLoading } = useQuery({ queryKey: qk.gorelo, queryFn: api.gorelo.status });
  const { busy, syncAll, applyRemovals } = useSync();
  const { confirm, element: confirmDialog } = useConfirm();

  const apply = async (p: PendingRemovals) => {
    const ok = await confirm({
      title: "Apply the removals?",
      body: `${pendingLine(p, "deleted for good")} Document links to them go too. Check in Gorelo first that they are really gone.`,
      confirmLabel: "Apply removals",
      danger: true,
    });
    if (ok) await applyRemovals();
  };

  const runs = status ? [status.lastFull, status.lastQuick].filter((r): r is SyncRun => r !== null) : [];
  return (
    <div>
      <PageTitle>Gorelo</PageTitle>
      {isLoading ? <Spinner label="Loading…" /> : null}
      {status ? (
        <div className="grid gap-4">
          <Panel
            aria-label="Connection"
            title="Connection"
            action={
              <Button variant="primary" disabled={busy || !status.configured} onClick={() => void syncAll()}>
                {busy ? "Syncing…" : "Sync now"}
              </Button>
            }
          >
            {status.configured ? (
              <p className="text-ink-2">
                Connected. Clients, their locations and their assets are read from Gorelo every ten minutes. Docs never
                writes to Gorelo.
              </p>
            ) : (
              <p className="text-ink-2">
                Not connected: no key is configured. The key is set on the server, as <code>GORELO_API_KEY</code> in the
                environment of docs-app, and never in this application.
              </p>
            )}
          </Panel>

          {status.pending ? (
            <Panel
              aria-label="Pending removals"
              title="Pending removals"
              action={
                <Button
                  variant="danger"
                  disabled={busy || !status.configured}
                  onClick={() => {
                    if (status.pending) void apply(status.pending);
                  }}
                >
                  Apply removals
                </Button>
              }
            >
              <p className="text-ink-2">The safety stop held these back. {pendingLine(status.pending)}</p>
            </Panel>
          ) : null}

          <Panel aria-label="Last passes" title="Last passes">
            <div className="overflow-x-auto">
              <table aria-label="Last passes" className="kd-datatable min-w-[560px]">
                <thead>
                  <tr>
                    <th>Pass</th>
                    <th>Started</th>
                    <th>Result</th>
                    <th>What changed</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-ink-3">
                        No pass has run yet.
                      </td>
                    </tr>
                  ) : null}
                  {runs.map((run) => (
                    <tr key={run.kind}>
                      <td>{KIND[run.kind]}</td>
                      <td className="tabular-nums">{formatDateTime(run.startedAt)}</td>
                      <td>{result(run)}</td>
                      <td>{syncChanges(run.counts) || "Nothing changed"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          {status.lastError ? (
            <Panel aria-label="Last error" title="Last error">
              <p className="text-danger">{status.lastError.error}</p>
              <p className="text-sm text-ink-3">
                {KIND[status.lastError.kind]} pass
                {status.lastError.at ? `, ${formatDateTime(status.lastError.at)}` : ""}
              </p>
            </Panel>
          ) : null}

          <Panel aria-label="Unlinked clients" title="Unlinked clients">
            {status.unlinked.length === 0 ? (
              <p className="text-ink-3">None.</p>
            ) : (
              <>
                <p className="text-ink-2">
                  These clients were typed into Docs before the sync. They are read-only. Delete them under{" "}
                  <Link to="/admin/topics" className="text-green-ink hover:underline">
                    Admin, Topics
                  </Link>
                  .
                </p>
                <ul className="grid gap-1">
                  {status.unlinked.map((c) => (
                    <li key={c.id} className="text-ink">
                      {c.name}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Panel>
        </div>
      ) : null}
      {confirmDialog}
    </div>
  );
}
